import { useCallback, useRef, useState } from 'react';
import { buildAddressLine, countryCodeFor, geocodeAddress } from '@repo/front-end-utils';
import type { LatLng } from '@repo/front-end-utils';

import Config from '../../common/Config';
import { ADDRESS_FIELDS, type TwinDetailsForm } from './twinDetailsForm';

/**
 * Turn the Twin details form's address into a latitude/longitude.
 *
 * An **accelerator, never a gate** (spec FR1): with no token, no address, or no match, the user
 * still types the coordinates by hand and the step still saves.
 *
 * `geocodeAddress` (`@repo/front-end-utils`, Mapbox Geocoding v5) takes a whole address line,
 * biases the search by country, and returns `null` on failure. One code path covers postcode-only
 * and full-address search, because Mapbox's forward geocoder handles a bare postcode perfectly
 * well. (The spec named a postcode-only helper that threw; it went with Edit mode's Twin tab.)
 *
 * Its default `timeoutMs` is 1000, a short wait suited to lookups nobody watches. This is an
 * interactive lookup where a person is watching, so it gets a real timeout. The parameter already
 * exists, so this reuses the helper unchanged.
 */

const INTERACTIVE_TIMEOUT_MS = 8000;

export type GeocodeState =
  | { status: 'idle' }
  | { status: 'searching' }
  | { status: 'found'; coords: LatLng; query: string }
  | { status: 'missed'; message: string };

export interface UseAddressGeocode {
  state: GeocodeState;
  /** True when a lookup is possible at all — a token exists and there is something to search. */
  canSearch: boolean;
  /** Why not, when `canSearch` is false. Shown as the action's tooltip. */
  disabledReason?: string;
  search: (form: TwinDetailsForm) => Promise<LatLng | null>;
  reset: () => void;
}

/** The public Mapbox token, in the order `MapCanvas` resolves it. */
export const resolveMapboxToken = (claimToken?: string): string =>
  Config.mapboxToken || claimToken || '';

export const addressQuery = (form: TwinDetailsForm): string =>
  buildAddressLine(ADDRESS_FIELDS.map(field => form[field]));

export const useAddressGeocode = (
  form: TwinDetailsForm,
  claimToken?: string,
): UseAddressGeocode => {
  const [state, setState] = useState<GeocodeState>({ status: 'idle' });
  const token = resolveMapboxToken(claimToken);
  const query = addressQuery(form);

  const canSearch = token !== '' && query !== '';
  const disabledReason = !token
    ? 'Address search is unavailable — no Mapbox token is configured for this environment. Enter the latitude and longitude directly.'
    : !query
      ? 'Enter an address or postcode to search for.'
      : undefined;

  // Latest wins: a lookup overtaken by a newer one, or by `reset`, still answers its caller but no
  // longer sets the outcome the card shows and announces (spec Failure States, Overtaken).
  const latestRef = useRef(0);

  const search = useCallback(
    async (current: TwinDetailsForm): Promise<LatLng | null> => {
      const currentQuery = addressQuery(current);
      if (!token || !currentQuery) return null;

      const id = ++latestRef.current;
      setState({ status: 'searching' });
      const coords = await geocodeAddress(
        currentQuery,
        token,
        // Lower-case, as the retired `countryToIso2` sent it: the same Mapbox request, and the
        // same key in `geocodeAddress`'s cache.
        countryCodeFor(current.country)?.toLowerCase(),
        INTERACTIVE_TIMEOUT_MS,
      );
      if (id !== latestRef.current) return coords;

      if (!coords) {
        // Deliberately does NOT clear whatever coordinates are already in the form: a failed
        // search must not take away a position the user (or an earlier search) had got right.
        setState({
          status: 'missed',
          message: `No match for “${currentQuery}”. Check the address, or enter the latitude and longitude directly.`,
        });
        return null;
      }

      setState({ status: 'found', coords, query: currentQuery });
      return coords;
    },
    [token],
  );

  const reset = useCallback(() => {
    latestRef.current += 1;
    setState({ status: 'idle' });
  }, []);

  return { state, canSearch, disabledReason, search, reset };
};
